import { supabase } from '../lib/supabase'

/**
 * Duplicate check points - Winnie's item 2: "VIN#, Rego, invoice numbers, Seller details,
 * bank account details."
 *
 * The five checks share one shape and one principle: a duplicate is reported, never
 * blocked. Every one of them has a legitimate repeat.
 *
 *   - A VIN can reappear because the same car came back through the yard.
 *   - A rego transfers between vehicles.
 *   - A supplier re-uses an invoice number across financial years.
 *   - The same seller sells three cars in a month, which is a good customer, not an error.
 *   - Two deals with one seller share a bank account by definition.
 *
 * Refusing those would teach people to work around the form, which is how you end up with
 * a VIN typed with a trailing space to get past a check. Naming them at the moment of
 * entry, with a link to the other record, lets a person decide in two seconds.
 *
 * Bank account numbers are matched but never returned. The comparison happens on the
 * normalised digits; what comes back is a masked tail. A duplicate warning does not need
 * to reprint somebody's account number on screen to be useful.
 */

export type DuplicateKind = 'vin' | 'rego' | 'invoice_ref' | 'seller' | 'bank_account'

export interface DuplicateHit {
  kind: DuplicateKind
  /** What matched, safe to display. Bank accounts arrive here already masked. */
  value: string
  /** The other record's id, so the screen can link to it. */
  otherId: string
  /** Human label for the other record. */
  otherLabel: string
  message: string
}

/* --------------------------------------------------------------------------------------
 * Normalisers. Exported because they are the part worth testing on their own.
 * ------------------------------------------------------------------------------------ */

/** A VIN is 17 characters and never contains I, O or Q - those are read as 1, 0 and 9. */
export function normVin(v: string | null | undefined): string {
  return String(v || '')
    .toUpperCase()
    .replace(/[^A-HJ-NPR-Z0-9]/g, '')
}

export function normRego(v: string | null | undefined): string {
  return String(v || '')
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')
}

export function normRef(v: string | null | undefined): string {
  return String(v || '')
    .toUpperCase()
    .replace(/\s+/g, ' ')
    .trim()
}

export function normDigits(v: string | null | undefined): string {
  return String(v || '').replace(/\D/g, '')
}

export function normEmail(v: string | null | undefined): string {
  return String(v || '').toLowerCase().trim()
}

/** Names differ by punctuation and legal suffixes far more often than by substance. */
export function normName(v: string | null | undefined): string {
  return String(v || '')
    .toLowerCase()
    .replace(/\b(pty|ltd|limited|proprietary|inc|the)\b/g, ' ')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
}

/**
 * An Australian mobile compared on its last nine digits, so +61 412 345 678,
 * 0412 345 678 and 0412345678 are one number rather than three.
 */
export function normMobile(v: string | null | undefined): string {
  const d = normDigits(v)
  return d.length >= 9 ? d.slice(-9) : d
}

/**
 * A bank account shown as a tail only.
 *
 * There is never a reason to render a full account number in a warning message, and a
 * duplicate check is not a good enough reason to start.
 */
export function maskAccount(bsb: string | null | undefined, account: string | null | undefined): string {
  const a = normDigits(account)
  if (!a) return '—'
  const tail = a.slice(-3)
  return `${normDigits(bsb) ? 'BSB ••• ••• · ' : ''}account ending ${tail}`
}

/* --------------------------------------------------------------------------------------
 * The checks
 * ------------------------------------------------------------------------------------ */

interface DealLite {
  id: string
  stock_number: string | null
  vehicle_vin: string | null
  autograb_vin: string | null
  registration_number: string | null
  vehicle_make: string | null
  vehicle_model: string | null
  autograb_make: string | null
  autograb_model: string | null
  seller_name: string | null
  seller_legal_name: string | null
  seller_email: string | null
  seller_mobile: string | null
  seller_phone: string | null
  seller_abn: string | null
  seller_bsb: string | null
  seller_account_number: string | null
  deleted_at: string | null
}

const labelOf = (d: DealLite) =>
  [
    d.stock_number ? `#${d.stock_number}` : null,
    [d.autograb_make || d.vehicle_make, d.autograb_model || d.vehicle_model].filter(Boolean).join(' '),
  ]
    .filter(Boolean)
    .join(' · ') || d.id.slice(0, 8)

export interface DuplicateQuery {
  vin?: string | null
  rego?: string | null
  invoiceRef?: string | null
  sellerName?: string | null
  sellerEmail?: string | null
  sellerMobile?: string | null
  sellerAbn?: string | null
  bsb?: string | null
  accountNumber?: string | null
  /** The deal being edited, so it does not report itself. */
  excludeDealId?: string | null
}

/**
 * Every duplicate the platform can see for one set of details.
 *
 * One read of `deals` covers four of the five checks, which is deliberate - a form that
 * fires five separate queries on every keystroke is a form people turn off.
 */
export async function findDuplicates(q: DuplicateQuery): Promise<DuplicateHit[]> {
  const hits: DuplicateHit[] = []
  const skip = q.excludeDealId || ''

  const wantVin = normVin(q.vin)
  const wantRego = normRego(q.rego)
  const wantEmail = normEmail(q.sellerEmail)
  const wantMobile = normMobile(q.sellerMobile)
  const wantAbn = normDigits(q.sellerAbn)
  const wantName = normName(q.sellerName)
  const wantAccount = normDigits(q.accountNumber)
  const wantBsb = normDigits(q.bsb)

  const needsDeals =
    wantVin.length >= 11 ||
    wantRego.length >= 4 ||
    wantEmail.length > 4 ||
    wantMobile.length >= 9 ||
    wantAbn.length === 11 ||
    wantName.length >= 4 ||
    wantAccount.length >= 5

  if (needsDeals) {
    const { data, error } = await supabase
      .from('deals')
      .select(
        'id, stock_number, vehicle_vin, autograb_vin, registration_number, vehicle_make, vehicle_model, autograb_make, autograb_model, seller_name, seller_legal_name, seller_email, seller_mobile, seller_phone, seller_abn, seller_bsb, seller_account_number, deleted_at',
      )
      .is('deleted_at', null)
    if (error) throw error

    for (const raw of (data || []) as unknown as DealLite[]) {
      if (raw.id === skip) continue
      const label = labelOf(raw)

      if (wantVin.length >= 11) {
        const other = normVin(raw.autograb_vin) || normVin(raw.vehicle_vin)
        if (other && other === wantVin) {
          hits.push({
            kind: 'vin',
            value: wantVin,
            otherId: raw.id,
            otherLabel: label,
            message: `This VIN is already on ${label}. Same car back through the yard, or a typo?`,
          })
        }
      }

      if (wantRego.length >= 4 && normRego(raw.registration_number) === wantRego) {
        hits.push({
          kind: 'rego',
          value: wantRego,
          otherId: raw.id,
          otherLabel: label,
          message: `Rego ${wantRego} is already on ${label}. Plates do transfer between cars — worth a look.`,
        })
      }

      /* Seller identity: any ONE of ABN, email or mobile is a real match. A name alone is
         not - there is more than one J Smith - so a name only counts alongside something
         else, and on its own it is not reported at all. */
      const sameAbn = wantAbn.length === 11 && normDigits(raw.seller_abn) === wantAbn
      const sameEmail = wantEmail.length > 4 && normEmail(raw.seller_email) === wantEmail
      const sameMobile =
        wantMobile.length >= 9 &&
        (normMobile(raw.seller_mobile) === wantMobile || normMobile(raw.seller_phone) === wantMobile)
      if (sameAbn || sameEmail || sameMobile) {
        const on = sameAbn ? 'ABN' : sameEmail ? 'email address' : 'mobile number'
        hits.push({
          kind: 'seller',
          value: raw.seller_name || raw.seller_legal_name || 'this seller',
          otherId: raw.id,
          otherLabel: label,
          message: `Same seller as ${label} — matched on ${on}. Fine if they are selling more than one car.`,
        })
      }

      /* Bank account: BSB and account together, or an account number long enough to be
         distinctive on its own. Compared on digits; never echoed back in full. */
      const sameAccount =
        wantAccount.length >= 5 &&
        normDigits(raw.seller_account_number) === wantAccount &&
        (!wantBsb || !normDigits(raw.seller_bsb) || normDigits(raw.seller_bsb) === wantBsb)
      if (sameAccount) {
        hits.push({
          kind: 'bank_account',
          value: maskAccount(raw.seller_bsb, raw.seller_account_number),
          otherId: raw.id,
          otherLabel: label,
          message:
            `The same bank account is on ${label}. Expected if it is the same seller — ` +
            'worth checking if it is not.',
        })
      }
    }
  }

  /* Invoice references live on the cost lines, not the deals. */
  const wantRefValue = normRef(q.invoiceRef)
  if (wantRefValue.length >= 3) {
    const { data, error } = await supabase
      .from('expenses')
      .select('id, deal_id, supplier, invoice_ref, incurred_on, amount')
      .eq('invoice_ref', String(q.invoiceRef || '').trim())
      .limit(5)
    if (error) throw error
    for (const raw of (data || []) as unknown as {
      id: string
      deal_id: string | null
      supplier: string | null
      incurred_on: string | null
    }[]) {
      hits.push({
        kind: 'invoice_ref',
        value: wantRefValue,
        otherId: raw.deal_id || raw.id,
        otherLabel: raw.supplier || 'an existing cost line',
        message:
          `Invoice ${wantRefValue} is already recorded` +
          (raw.supplier ? ` against ${raw.supplier}` : '') +
          (raw.incurred_on ? ` on ${raw.incurred_on}` : '') +
          '. Check it is not the same bill twice.',
      })
    }
  }

  /* One line per kind: five copies of "same seller" is noise, the first is the signal. */
  const seen: Record<string, boolean> = {}
  return hits.filter((h) => {
    const k = h.kind + '|' + h.otherId
    if (seen[k]) return false
    seen[k] = true
    return true
  })
}
